/** 与后端 FeatureModulesVO / aipd.features 对齐（无 HTTP 依赖，可供 http 拦截器引用） */
export interface FeatureModules {
  knowledgeEnabled: boolean;
  aiToolsEnabled: boolean;
  projectManagementEnabled: boolean;
  smartDocumentEnabled: boolean;
}

export type FeatureModuleKey =
  | 'knowledge'
  | 'aiTools'
  | 'projectManagement'
  | 'smartDocument';

/** 与 application.yml 默认一致：知识空间/AI工具开，项目管理/智能文档关 */
export const DEFAULT_FEATURE_MODULES: FeatureModules = {
  knowledgeEnabled: true,
  aiToolsEnabled: true,
  projectManagementEnabled: false,
  smartDocumentEnabled: false,
};

let cached: FeatureModules | null = null;

export function getFeatureModules(): FeatureModules {
  return cached ?? DEFAULT_FEATURE_MODULES;
}

export function hasFeatureModulesCache(): boolean {
  return cached != null;
}

export function setFeatureModulesCache(modules: FeatureModules): void {
  cached = modules;
}

export function isFeatureEnabled(key: FeatureModuleKey, modules = getFeatureModules()): boolean {
  switch (key) {
    case 'knowledge':
      return modules.knowledgeEnabled;
    case 'aiTools':
      return modules.aiToolsEnabled;
    case 'projectManagement':
      return modules.projectManagementEnabled;
    case 'smartDocument':
      return modules.smartDocumentEnabled;
    default:
      return false;
  }
}

/** 根据 /api 相对路径判断所属功能模块（与后端 FeatureModulesFilter 对齐） */
export function featureModuleForApiPath(url: string): FeatureModuleKey | null {
  const path = String(url || '').split('?')[0];
  const p = path.startsWith('/api/') ? path.slice(4) : path.replace(/^\/+/, '');
  const normalized = `/${p}`.replace(/\/+/g, '/');

  if (normalized.startsWith('/projects/member-candidates')) {
    // 组织管理「转移所有权」等复用，不随项目管理开关短路
    return null;
  }
  if (
    normalized.startsWith('/projects')
    || normalized.startsWith('/requirements')
    || normalized.startsWith('/dashboard')
    || normalized.startsWith('/components')
    || normalized.startsWith('/v1/documents/comments')
  ) {
    return 'projectManagement';
  }
  if (normalized.startsWith('/knowledge') || normalized.startsWith('/knowledge-space')) {
    return 'knowledge';
  }
  if (
    normalized.startsWith('/ai-tools')
    || normalized.startsWith('/ai/calculators')
    || normalized.startsWith('/ai/calculator')
    || normalized.startsWith('/calculator-shares')
    || normalized.startsWith('/public/calculator-shares')
    || normalized.startsWith('/lw-engine')
    || normalized.startsWith('/ai-review')
    || normalized.startsWith('/v1/review')
  ) {
    return 'aiTools';
  }
  if (
    normalized.startsWith('/v1/document-templates')
    || normalized.startsWith('/v1/document-skills')
    || normalized.startsWith('/v1/skill-executions')
    || /\/v1\/projects\/[^/]+\/document-workbench/.test(normalized)
    || /\/v1\/projects\/[^/]+\/document-templates/.test(normalized)
    || /\/v1\/projects\/[^/]+\/documents/.test(normalized)
    || /\/v1\/projects\/[^/]+\/document-skills/.test(normalized)
    || /\/v1\/projects\/[^/]+\/skill-executions/.test(normalized)
    || /\/v1\/projects\/[^/]+\/template-generations/.test(normalized)
    || (normalized.startsWith('/v1/documents/') && !normalized.startsWith('/v1/documents/comments'))
  ) {
    return 'smartDocument';
  }
  return null;
}
